import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Installation } from '../../Services/EntiteProjet';
import { EtatService } from '../../Services/etat';

type MapInstance = import('maplibre-gl').Map;
type MapLibreModule = typeof import('maplibre-gl');

/**
 * Composant réutilisable affichant une carte MapLibre avec clustering.
 *
 * Reçoit une liste d'installations en entrée et gère en autonomie :
 * - le chargement lazy de maplibre-gl,
 * - la création de la source GeoJSON et des couches (clusters, points),
 * - la colorisation des clusters selon le niveau le plus critique,
 * - les popups au clic (cluster et point isolé),
 * - le recadrage automatique sur les données,
 * - l'ouverture d'une modale plein-écran avec une seconde instance de carte.
 */
@Component({
  selector: 'app-carte-installations',
  imports: [CommonModule],
  templateUrl: './carte-installations.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './carte-installations.css',
})
export class CarteInstallationsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') private mapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('mapContainerModal') private mapContainerModal?: ElementRef<HTMLDivElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private mapLibre?: MapLibreModule;

  // Carte principale
  private map?: MapInstance;
  private mapInitialisee = false;

  // Carte modale
  private mapModal?: MapInstance;
  private mapModalInitialisee = false;

  private readonly sourceId = 'installations-source';
  private readonly clustersLayerId = 'clusters-layer';
  private readonly clusterCountLayerId = 'cluster-count-layer';
  private readonly unclusteredLayerId = 'unclustered-point-layer';

  installations = input<Installation[]>([]);
  modalVisible = false;

  /** Légende des niveaux d'état, affichée dans la carte plein écran. */
  readonly legendeNiveaux = Object.entries(EtatService.NIVEAU_INFOS).map(([niveau, infos]) => ({
    niveau: Number(niveau),
    ...infos,
  }));

  installationsAvecCoordonnees = computed(() =>
    this.installations().filter((i) => this.coordonneesValides(i)),
  );

  constructor() {
    // Réagit aux changements d'installations : met à jour toutes les instances de carte actives.
    effect(() => {
      const installations = this.installationsAvecCoordonnees();
      if (this.mapInitialisee && this.map && this.mapLibre) {
        this.nettoyerSources(this.map);
        this.creerSourceGeoJSON(this.map, this.mapLibre, installations);
        this.cadrerCarte(this.map, this.mapLibre, installations);
      }
      if (this.mapModalInitialisee && this.mapModal && this.mapLibre) {
        this.nettoyerSources(this.mapModal);
        this.creerSourceGeoJSON(this.mapModal, this.mapLibre, installations);
        this.cadrerCarte(this.mapModal, this.mapLibre, installations);
      }
    });
  }

  /**
   * Initialise la carte principale de manière asynchrone après le rendu de la vue.
   */
  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId) || !this.mapContainer) return;

    this.mapLibre = await this.chargerMapLibre();
    this.map = this.creerInstanceCarte(this.mapContainer.nativeElement, this.mapLibre);

    this.map.on('load', () => {
      this.mapInitialisee = true;
      this.fermerAttribution(this.mapContainer!.nativeElement);
      if (this.map && this.mapLibre) {
        this.creerSourceGeoJSON(this.map, this.mapLibre, this.installationsAvecCoordonnees());
        this.cadrerCarte(this.map, this.mapLibre, this.installationsAvecCoordonnees());
      }
    });
  }

  /**
   * Nettoyage : supprime les deux instances de carte pour éviter les fuites mémoire WebGL.
   */
  ngOnDestroy() {
    if (this.map) {
      this.nettoyerSources(this.map);
      this.map.remove();
    }
    if (this.mapModal) {
      this.nettoyerSources(this.mapModal);
      this.mapModal.remove();
    }
  }

  // ─── Modale ────────────────────────────────────────────────────────────────

  /** Affiche la modale plein-écran et initialise la carte modale après le rendu du DOM. */
  ouvrirModal() {
    this.modalVisible = true;
    // Attendre qu'Angular rende le DOM modal avant d'initialiser la seconde carte.
    setTimeout(() => this.initialiserCarteModal(), 0);
  }

  /** Masque la modale et détruit l'instance de carte modale pour libérer les ressources WebGL. */
  fermerModal() {
    this.modalVisible = false;
    if (this.mapModal) {
      this.nettoyerSources(this.mapModal);
      this.mapModal.remove();
      this.mapModal = undefined;
      this.mapModalInitialisee = false;
    }
  }

  /** Ferme la modale si le clic provient du backdrop (et non du contenu intérieur). */
  fermerModalSurBackdrop(event: MouseEvent) {
    if (event.target === event.currentTarget) this.fermerModal();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.modalVisible) this.fermerModal();
  }

  /** Délègue la navigation Angular pour les liens de popup injectés en HTML brut. */
  @HostListener('click', ['$event'])
  onInstallationLinkClick(event: MouseEvent) {
    const link = (event.target as HTMLElement).closest(
      '[data-installation-id]',
    ) as HTMLElement | null;
    if (!link) return;
    event.preventDefault();
    const id = link.getAttribute('data-installation-id');
    if (id) void this.router.navigate(['/installation', id]);
  }

  /** Crée et configure la carte MapLibre dans le conteneur de la modale. */
  private async initialiserCarteModal() {
    if (!isPlatformBrowser(this.platformId) || !this.mapContainerModal) return;
    if (!this.mapLibre) this.mapLibre = await this.chargerMapLibre();

    this.mapModal = this.creerInstanceCarte(this.mapContainerModal.nativeElement, this.mapLibre);

    this.mapModal.on('load', () => {
      this.mapModalInitialisee = true;
      if (this.mapModal && this.mapLibre) {
        this.creerSourceGeoJSON(this.mapModal, this.mapLibre, this.installationsAvecCoordonnees());
        this.cadrerCarte(this.mapModal, this.mapLibre, this.installationsAvecCoordonnees());
      }
    });
  }

  // ─── Helpers MapLibre (acceptent l'instance en paramètre) ──────────────────

  /**
   * Charge maplibre-gl et normalise le module retourné : selon la façon dont l'import
   * dynamique de ce package UMD est interopéré (esbuild en build de prod vs Vite en dev),
   * les exports nommés (`Map`, `NavigationControl`, ...) se retrouvent soit directement
   * sur le module, soit sous `.default`.
   */
  private async chargerMapLibre(): Promise<MapLibreModule> {
    const mod: any = await import('maplibre-gl');
    return mod?.Map ? mod : mod.default;
  }

  /**
   * Instancie une carte MapLibre dans le conteneur donné avec les contrôles par défaut.
   */
  private creerInstanceCarte(container: HTMLDivElement, mapLibre: MapLibreModule): MapInstance {
    const map = new mapLibre.Map({
      container,
      style: 'https://tiles.openfreemap.org/styles/positron',
      center: [2.2137, 46.2276],
      zoom: 4.4,
      attributionControl: false,
    });
    map.addControl(new mapLibre.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new mapLibre.AttributionControl({ compact: true }), 'bottom-right');
    return map;
  }

  /** Retourne '#fff' ou '#1e1e1e' selon la luminance de `couleurFond`, pour un texte toujours lisible. */
  private couleurTexteLisible(couleurFond: string): string {
    const hex = couleurFond.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 150 ? '#1e1e1e' : '#fff';
  }

  /**
   * Crée la source GeoJSON (avec clustering activé) et les trois couches associées
   * sur l'instance de carte fournie.
   *
   * - `clusters-layer` : cercles colorés par le niveau le plus critique du cluster.
   * - `cluster-count-layer` : étiquette du nombre de points dans chaque cluster.
   * - `unclustered-point-layer` : points individuels colorés selon leur couleur d'état.
   */
  private creerSourceGeoJSON(
    map: MapInstance,
    mapLibre: MapLibreModule,
    installations: Installation[],
  ) {
    // Expressions MapLibre 'match' sur min_rang (priorité d'affichage du cluster)
    // → couleur canonique du niveau, et → couleur de texte lisible sur cette couleur.
    const clusterColorExpr: unknown[] = ['match', ['get', 'min_rang']];
    const clusterTextColorExpr: unknown[] = ['match', ['get', 'min_rang']];
    for (const [niveau, infos] of Object.entries(EtatService.NIVEAU_INFOS)) {
      const rang = EtatService.rangNiveau(Number(niveau));
      clusterColorExpr.push(rang, infos.couleur);
      clusterTextColorExpr.push(rang, this.couleurTexteLisible(infos.couleur));
    }
    clusterColorExpr.push('#bebebe'); // fallback
    clusterTextColorExpr.push(this.couleurTexteLisible('#bebebe')); // fallback

    const features = installations.map((installation) => {
      const niveau = installation.etat?.niveau ?? 0;
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [installation.lon, installation.lat] },
        properties: {
          id: installation.id,
          nom: installation.nom,
          ville: installation.ville ?? '',
          niveau,
          rang: EtatService.rangNiveau(niveau),
          couleur: EtatService.NIVEAU_INFOS[niveau]?.couleur ?? '#bebebe',
        },
      };
    });

    map.addSource(this.sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: { min_rang: ['min', ['get', 'rang']] },
    });

    map.addLayer({
      id: this.clustersLayerId,
      type: 'circle',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': clusterColorExpr as any,
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40] as any,
      },
    });

    map.addLayer({
      id: this.clusterCountLayerId,
      type: 'symbol',
      source: this.sourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        // Police disponible dans les glyphes du fond OpenFreeMap Positron.
        'text-font': ['Noto Sans Regular'],
        'text-size': 12,
      },
      paint: {
        'text-color': clusterTextColorExpr as any,
      },
    });

    map.addLayer({
      id: this.unclusteredLayerId,
      type: 'circle',
      source: this.sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['get', 'couleur'],
        'circle-radius': 6,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      },
    });

    this.ajouterEcouteursEvenements(map, mapLibre);
  }

  /**
   * Enregistre les écouteurs d'événements MapLibre sur l'instance fournie :
   * clic cluster, clic point isolé, survol.
   */
  private ajouterEcouteursEvenements(map: MapInstance, mapLibre: MapLibreModule) {
    map.on('click', this.clustersLayerId, async (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [this.clustersLayerId] });
      if (!features.length) return;

      const clusterId = features[0].properties?.['cluster_id'];
      const leaves = await (map.getSource(this.sourceId) as any).getClusterLeaves(
        clusterId,
        Infinity,
      );

      new mapLibre.Popup()
        .setLngLat((features[0].geometry as any).coordinates)
        .setHTML(this.genererHtmlCluster(leaves))
        .addTo(map);
    });

    map.on('click', this.unclusteredLayerId, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [this.unclusteredLayerId] });
      if (!features.length) return;

      const coordinates = (
        (features[0].geometry as any).coordinates as [number, number]
      ).slice() as [number, number];
      const props = features[0].properties;

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      new mapLibre.Popup()
        .setLngLat(coordinates)
        .setHTML(
          `<a class="popup-install-link" data-installation-id="${props?.['id'] ?? ''}" href="#">
             <strong>${this.escapeHtml(props?.['nom'] ?? '')}</strong>
           </a><br>
           ${this.escapeHtml(props?.['ville'] ?? '')}`,
        )
        .addTo(map);
    });

    map.on('mouseenter', this.clustersLayerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', this.clustersLayerId, () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', this.unclusteredLayerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', this.unclusteredLayerId, () => {
      map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Ajuste le viewport de la carte fournie pour englober toutes les installations.
   */
  private cadrerCarte(map: MapInstance, mapLibre: MapLibreModule, installations: Installation[]) {
    if (installations.length === 0) return;

    const bounds = new mapLibre.LngLatBounds();
    for (const installation of installations) {
      bounds.extend([installation.lon, installation.lat]);
    }

    if (installations.length === 1) {
      map.flyTo({
        center: [installations[0].lon, installations[0].lat],
        zoom: 11,
        essential: true,
      });
      return;
    }

    map.fitBounds(bounds, { padding: 28, maxZoom: 12, duration: 500 });
  }

  /**
   * Supprime les couches GeoJSON et la source de la carte fournie.
   */
  private nettoyerSources(map: MapInstance) {
    if (map.getLayer(this.clustersLayerId)) map.removeLayer(this.clustersLayerId);
    if (map.getLayer(this.clusterCountLayerId)) map.removeLayer(this.clusterCountLayerId);
    if (map.getLayer(this.unclusteredLayerId)) map.removeLayer(this.unclusteredLayerId);
    if (map.getSource(this.sourceId)) map.removeSource(this.sourceId);
  }

  /**
   * Vérifie qu'une installation possède des coordonnées géographiques exploitables.
   */
  private coordonneesValides(installation: Installation): boolean {
    return (
      Number.isFinite(installation.lat) &&
      Number.isFinite(installation.lon) &&
      Math.abs(installation.lat) <= 90 &&
      Math.abs(installation.lon) <= 180 &&
      !(installation.lat === 0 && installation.lon === 0)
    );
  }

  /**
   * Génère le HTML de la popup d'un cluster (installations triées par priorité d'affichage).
   */
  private genererHtmlCluster(features: any[]): string {
    const installations = features
      .map((f) => f.properties)
      .sort((a, b) => EtatService.rangNiveau(a?.['niveau'] ?? 0) - EtatService.rangNiveau(b?.['niveau'] ?? 0));

    const items = installations
      .map(
        (props) => `
        <div style="display:flex;align-items:center;padding:6px 0;border-bottom:1px solid #eee;">
          <span style="width:10px;height:10px;border-radius:50%;background:${props?.['couleur'] ?? '#bebebe'};margin-right:8px;flex-shrink:0;"></span>
          <div style="flex:1;">
            <a class="popup-install-link" data-installation-id="${props?.['id'] ?? ''}" href="#" style="color:inherit;text-decoration:none;font-weight:700;">
              ${this.escapeHtml(props?.['nom'] ?? 'Sans nom')}
            </a><br>
            <small style="color:#666;">${this.escapeHtml(props?.['ville'] ?? '')}</small>
          </div>
        </div>`,
      )
      .join('');

    return `<div style="max-height:300px;overflow-y:auto;max-width:250px;">
      <div style="font-weight:bold;margin-bottom:8px;padding-bottom:8px;border-bottom:2px solid #333;">
        ${installations.length} installation${installations.length > 1 ? 's' : ''}
      </div>
      ${items}
    </div>`;
  }

  /**
   * Échappe les caractères spéciaux HTML pour prévenir les injections XSS dans les popups.
   */
  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  /**
   * Replie le panneau d'attribution après le chargement initial de la carte.
   */
  private fermerAttribution(container: HTMLDivElement) {
    container.querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
  }
}
